import {
  queryOptions,
  skipToken,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { VestigasHTTPError } from '@vestigas/data';

import { queryKeysSupplier } from '../queryKeys';

import { fetchSupplierActivationPublic } from './fetchSupplierActivationPublic';
import { type SupplierActivationPublicResponse } from './types';

/**
 * Generates the query options for the public supplier activation query.
 *
 * @param reqId - The activation request ID.
 * @param token - The magic link token.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and staleTime.
 */
export const getSupplierActivationPublicQueryOptions = <
  TData = SupplierActivationPublicResponse,
>({
  reqId,
  token,
  options,
}: {
  reqId: UUID | undefined;
  token: string | undefined;
  options?: Omit<
    UseQueryOptions<SupplierActivationPublicResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn:
      reqId && token
        ? async ({ signal }: QueryFunctionContext) =>
            fetchSupplierActivationPublic(reqId, token, { signal })
        : skipToken,
    queryKey: queryKeysSupplier.getActivationPublic(
      reqId ?? '',
      token ? '__present__' : '__missing__',
    ),
    staleTime: ms('60s'),
    throwOnError: false,
    ...options,
  });

/**
 * React Query based custom hook for viewing a supplier activation request via
 * magic link (public, no authentication).
 *
 * The query is skipped until both `reqId` and `token` are defined.
 *
 * @param reqId - The activation request ID.
 * @param token - The magic link token from the supplier's email.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers-Activation-(Public)/operation/get_supplier_activation_public_api_v1_suppliers_activation__req_id__get
 */
export const useQuerySupplierActivationPublic = <
  TData = SupplierActivationPublicResponse,
>(
  {
    reqId,
    token,
  }: {
    reqId: UUID | undefined;
    token: string | undefined;
  },
  options?: Omit<
    UseQueryOptions<SupplierActivationPublicResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getSupplierActivationPublicQueryOptions<TData>({ reqId, token, options }),
  );
